import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Progress,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';

const clamp = (value) => Math.min(100, Math.max(0, value || 0));

const RevenueShare = () => {
  const [adminShare, setAdminShare] = useState(70);
  const [creatorShare, setCreatorShare] = useState(30);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    fetchConfig();
  }, []);

  const getApiBase = () => {
    const raw =
      process.env.REACT_APP_API_BASE_URL ||
      process.env.REACT_APP_BASE_URL ||
      window.location.origin || // fallback to current host
      'http://localhost:3030';

    return raw.trim().replace(/\/$/, '');
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Unauthorized');
      }

      const response = await axios.get(
        `${getApiBase()}/api/admin/revenue-share`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const data = response.data.data || {};
        setAdminShare(data.adminShare ?? 70);
        setCreatorShare(data.creatorShare ?? 30);
      } else {
        throw new Error('Unable to fetch revenue share');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminChange = (_, valueNumber) => {
    const adminValue = clamp(valueNumber);
    setAdminShare(adminValue);
    setCreatorShare(Number((100 - adminValue).toFixed(2)));
  };

  const handleCreatorChange = (_, valueNumber) => {
    const creatorValue = clamp(valueNumber);
    setCreatorShare(creatorValue);
    setAdminShare(Number((100 - creatorValue).toFixed(2)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const total = Number(adminShare) + Number(creatorShare);
    if (Math.abs(total - 100) > 0.01) {
      setError('Admin share and creator share must add up to 100%');
      return;
    }

    try {
      setSaving(true);
      const token =
        localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (!token) {
        throw new Error('Unauthorized');
      }

      await axios.put(
        `${getApiBase()}/api/admin/revenue-share`,
        {
          adminShare: Number(adminShare),
          creatorShare: Number(creatorShare),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast({
        title: 'Revenue share updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      const message = err.response?.data?.message || err.message;
      setError(message);
      toast({
        title: 'Unable to update revenue share',
        description: message,
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={6}>
      <Heading size="lg" mb={6}>
        Revenue Share Settings
      </Heading>

      {error && !saving && (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          <AlertTitle mr={2}>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card bg={bgColor} border="1px" borderColor={borderColor} shadow="md">
        <CardHeader>
          <Heading size="md">Set Admin & Creator Share</Heading>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Choose how each chapter audio purchase is split. Total must be
            100%.
          </Text>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Flex gap={6} direction={{ base: 'column', md: 'row' }}>
              <FormControl isRequired>
                <FormLabel>Admin Share (%)</FormLabel>
                <NumberInput
                  value={adminShare}
                  onChange={handleAdminChange}
                  min={0}
                  max={100}
                  precision={2}
                  isDisabled={loading}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Creator Share (%)</FormLabel>
                <NumberInput
                  value={creatorShare}
                  onChange={handleCreatorChange}
                  min={0}
                  max={100}
                  precision={2}
                  isDisabled={loading}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </Flex>

            <Box mt={6}>
              <Text fontWeight="medium" mb={2}>
                Live Split
              </Text>
              <Progress
                value={adminShare}
                size="md"
                colorScheme="blue"
                borderRadius="md"
                bg="gray.100"
              />
              <Flex justify="space-between" mt={2}>
                <Text color="blue.500">Admin {adminShare}%</Text>
                <Text color="green.500">Creator {creatorShare}%</Text>
              </Flex>
            </Box>

            <Flex mt={6} justify="flex-end" gap={3}>
              <Button
                variant="outline"
                onClick={fetchConfig}
                isDisabled={loading || saving}
              >
                Reset
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                isLoading={saving}
                loadingText="Saving..."
              >
                Save Share
              </Button>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default RevenueShare;
